/**
 * QuickBooks + design-fee subscriptions and daily sync for Project P&L.
 * Used by the Finances hub section — not a standalone page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { db } from '../utils/firebase';
import type { Payment, QBExpense } from '../types';
import type { DesignFeePaymentLite } from '../utils/projectPL';
import { canAccessProjectPL } from '../utils/projectPLAccess';
import { filterExpensesForViewer } from '../utils/shortTermOps';
import { qbExpensesProjectPLQuery } from '../utils/qbExpensesQueries';
import { crmAuthedFetch } from '../utils/crmAuthedFetch';

const QB_SYNC_DOC_PATH = 'appSettings/quickbooksSync';
const AUTO_SYNC_STALE_MS = 20 * 60 * 60 * 1000;

export type DesignFeePaymentRow = Required<Pick<DesignFeePaymentLite, 'amount' | 'paidAt'>> &
  DesignFeePaymentLite & {
    portalId: string;
    clientName: string;
  };

export function useProjectPLWorkspace() {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const currentCRMUser = useMemo(
    () => crmUsers.find((u) => u.uid === user?.id),
    [crmUsers, user?.id],
  );
  const canAccess = useMemo(
    () =>
      canAccessProjectPL({
        authRole: user?.role,
        designations: currentCRMUser?.designations ?? user?.designations,
      }),
    [currentCRMUser?.designations, user?.designations, user?.role],
  );
  const isAdmin = user?.role === 'admin';

  const [qbPayments, setQbPayments] = useState<(Payment & { id: string })[]>([]);
  const [qbExpenses, setQbExpenses] = useState<(QBExpense & { id: string })[]>([]);
  const [designFeePayments, setDesignFeePayments] = useState<DesignFeePaymentRow[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [lastSyncLoaded, setLastSyncLoaded] = useState(false);

  useEffect(() => {
    if (!user || !canAccess) return;
    const paymentsQuery = collection(db, 'payments');
    const unsubPayments = onSnapshot(paymentsQuery, (snapshot) => {
      const data = snapshot.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          createdAt: raw.createdAt?.toDate?.() || (raw.createdAt ? new Date(raw.createdAt) : undefined),
          paidAt: raw.paidAt?.toDate?.() || (raw.paidAt ? new Date(raw.paidAt) : undefined),
        } as Payment & { id: string };
      });
      setQbPayments(data);
    });

    const expensesQuery = qbExpensesProjectPLQuery();
    const unsubExpenses = onSnapshot(expensesQuery, (snapshot) => {
      const data = snapshot.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          syncedAt: raw.syncedAt?.toDate?.() || new Date(),
          createdAt: raw.createdAt?.toDate?.() || new Date(),
        } as QBExpense & { id: string };
      });
      setQbExpenses(filterExpensesForViewer(data, isAdmin));
    });

    return () => {
      unsubPayments();
      unsubExpenses();
    };
  }, [canAccess, isAdmin, user]);

  useEffect(() => {
    if (!user || !canAccess) return;
    const q = query(collection(db, 'clientPortals'), where('designFeePaid', '==', true));
    const unsub = onSnapshot(q, (snapshot) => {
      const rows: DesignFeePaymentRow[] = [];
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const projectsMap = (data.projects || {}) as Record<string, any>;
        const projectRows: DesignFeePaymentRow[] = [];

        for (const [projId, proj] of Object.entries(projectsMap)) {
          if (proj.designFeePaid && proj.amountPaid && proj.designFeePaidAt) {
            const projPaidAt = proj.designFeePaidAt?.toDate?.() || new Date(proj.designFeePaidAt);
            projectRows.push({
              portalId: d.id,
              clientName: `${data.clientName || 'Unknown'} (${proj.projectName || projId})`,
              amount: proj.amountPaid,
              paidAt: projPaidAt,
              dealId: projId,
            });
          }
        }

        if (projectRows.length > 0) {
          rows.push(...projectRows);
          return;
        }

        const paidAt =
          data.designFeePaidAt?.toDate?.() ||
          (data.designFeePaidAt ? new Date(data.designFeePaidAt) : null);
        if (data.amountPaid && paidAt) {
          rows.push({
            portalId: d.id,
            clientName: data.clientName || 'Unknown',
            amount: data.amountPaid,
            paidAt,
            dealId: data.dealId,
          });
        }
      });
      setDesignFeePayments(rows);
    });
    return () => unsub();
  }, [canAccess, user]);

  useEffect(() => {
    if (!user || !canAccess) return;
    const unsub = onSnapshot(
      doc(db, QB_SYNC_DOC_PATH),
      (snap) => {
        const ts = snap.exists() ? snap.data()?.lastSyncAt : null;
        setLastSyncAt(ts?.toDate?.() || (ts ? new Date(ts) : null));
        setLastSyncLoaded(true);
      },
      () => setLastSyncLoaded(true),
    );
    return () => unsub();
  }, [canAccess, user]);

  const triggerSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const projectId = 'lctf-projects';
      const res = await crmAuthedFetch(
        `https://us-central1-${projectId}.cloudfunctions.net/syncQuickBooksHistory`,
        { method: 'POST' },
      );
      const data = await res.json();
      if (data.success) {
        const exp = data.results.expenses;
        const expStr = exp ? `, ${exp.synced} expenses` : '';
        setSyncResult(
          `Synced ${data.results.invoices.synced} invoices, ${data.results.payments.synced} payments${expStr} (${data.results.matched} matched to deals)`,
        );
        try {
          await setDoc(
            doc(db, QB_SYNC_DOC_PATH),
            {
              lastSyncAt: serverTimestamp(),
              lastSyncBy: user?.id || null,
              lastSyncSummary: {
                invoices: data.results.invoices.synced,
                payments: data.results.payments.synced,
                expenses: data.results.expenses?.synced ?? 0,
                matched: data.results.matched,
              },
            },
            { merge: true },
          );
        } catch (writeErr) {
          console.warn('Failed to write QB sync timestamp:', writeErr);
        }
      } else if (data.error === 'QuickBooks not connected') {
        setSyncResult('QuickBooks is not connected. Connect via Settings first.');
      } else if (data.error?.includes('access token')) {
        setSyncResult('QuickBooks token expired — reconnect via Settings.');
      } else {
        setSyncResult(`Error: ${data.error}`);
      }
    } catch (err) {
      setSyncResult(`Sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSyncing(false);
    }
  }, [user?.id]);

  const autoSyncAttemptedRef = useRef(false);
  useEffect(() => {
    if (!user || !canAccess || !lastSyncLoaded || autoSyncAttemptedRef.current) return;
    if (syncing) return;
    const isStale = !lastSyncAt || Date.now() - lastSyncAt.getTime() > AUTO_SYNC_STALE_MS;
    if (!isStale) return;
    autoSyncAttemptedRef.current = true;
    triggerSync();
  }, [user, canAccess, lastSyncLoaded, lastSyncAt, syncing, triggerSync]);

  return {
    canAccess,
    qbPayments,
    qbExpenses,
    designFeePayments,
    syncing,
    syncResult,
    lastSyncAt,
    triggerSync,
  };
}
